import React from "react";

import { FormattedMessage, useIntl } from "react-intl";

import Button, { LoadingButton } from "@atlaskit/button";
import ButtonGroup from "@atlaskit/button/button-group";
import Form, {
    ErrorMessage,
    Field,
    HelperMessage,
    RequiredAsterisk
} from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import Modal, {
    ModalBody,
    ModalHeader,
    ModalFooter,
    ModalTransition
} from "@atlaskit/modal-dialog";
import { Text, Stack, Box } from "@atlaskit/primitives";
import Select, { type ValueType } from "@atlaskit/select";
import Spinner from "@atlaskit/spinner";
import TextArea from "@atlaskit/textarea";

import { CloseModalButton } from "@/components/product-demos/jsw-interactive-board/src/components/close-modal-button";
import {
    IBxpWebStargateClient,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { useFireAnalyticsEvents } from "../../../../../analytics";
import {
    SourceKey,
    ActionKey,
    ActionSubjectKey,
    ActionSubjectIdKey
} from "../../../../../analytics/constants";
import Flag, { CustomFlagProps } from "../../../../contexts/FlagProvider";
import { SAC_URL } from "../../migration-status-table/constants";
import { extractValueFromUnmappedField } from "../../migration-status-table/utils/get-table-rows";
import { useTicketsFilteredBySelectedOrg } from "../../utils/use-tickets-filtered-by-selected-org";

import { createMoveTicketComment } from "./comment-mutation-query";
import { messages } from "./messages";

interface Option {
    label: string;
    site: string;
    product: string;
    value: string;
}

const FastshiftAssistanceModalForm = ({
    handleCloseModal
}: {
    handleCloseModal: () => void;
}) => {
    const intl = useIntl();
    const stargateClient = useBxpWebStargateClient();
    const { showFlag } = React.useContext(Flag.Context);
    const { fireUIEvent } = useFireAnalyticsEvents();
    const isModalExtendedSearchEnabled = checkFeatureFlagGate(
        featureFlags.sxpFastshiftPortalModalExtendedSearch
    );

    const {
        filteredTickets: customerSupportRequests,
        isPending: isCustomerSupportRequestsLoading
    } = useTicketsFilteredBySelectedOrg();

    const sendClickSubmitButtonEvent = (supportTicketId: string) => {
        fireUIEvent({
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.BUTTON,
            actionSubjectId:
                ActionSubjectIdKey.REQUEST_FASTSHIFT_ASSISTANCE_MODAL_SUBMIT_BUTTON_CLICK,
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            attributes: {
                supportTicketId
            }
        });
    };

    const sendDisplayFlagEvent = ({
        supportTicketId,
        success
    }: {
        supportTicketId: string;
        success: boolean;
    }) => {
        fireUIEvent({
            action: ActionKey.DISPLAYED,
            actionSubject: ActionSubjectKey.FLAG,
            actionSubjectId:
                ActionSubjectIdKey.REQUEST_FASTSHIFT_ASSISTANCE_COMMENT_CONFIRMATION_FLAG,
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            attributes: {
                supportTicketId,
                successFlag: success
            }
        });
    };

    const customerSupportRequestOptions: Option[] = React.useMemo(
        () =>
            customerSupportRequests
                .filter((ticket) => ticket.status.category === "OPEN")
                .map((ticket) => {
                    const productName =
                        extractValueFromUnmappedField(
                            ticket.migrationProducts
                        ) || "";

                    let siteDomainName = "";
                    try {
                        siteDomainName = ticket.destinationLicense
                            ? new URL(ticket.destinationLicense).hostname
                            : "";
                    } catch {
                        siteDomainName = "";
                    }
                    return {
                        label: `${ticket.id} - ${ticket.summary}`,
                        site: siteDomainName,
                        product: productName,
                        value: ticket.id
                    };
                }),
        [customerSupportRequests]
    );

    const successFlag = React.useCallback(
        ({ ticketId }: { ticketId: string }): CustomFlagProps => {
            return {
                id: "success-flag",
                iconType: "success",
                styleOverride: true,
                autoDismiss: true,
                title: intl.formatMessage(
                    messages.fastshiftAssistanceModalSuccessFlagTitle
                ),
                description: intl.formatMessage(
                    messages.fastshiftAssistanceModalSuccessFlagDescription
                ),
                actions: [
                    {
                        content: intl.formatMessage(
                            messages.fastshiftAssistanceModalSuccessFlagAction
                        ),
                        href: `${SAC_URL}/requests/${ticketId}`,
                        target: "_blank"
                    }
                ]
            };
        },
        [intl]
    );

    const errorFlag = React.useMemo((): CustomFlagProps => {
        return {
            id: "error-flag",
            iconType: "error",
            styleOverride: true,
            autoDismiss: true,
            title: intl.formatMessage(
                messages.fastshiftAssistanceModalErrorFlagTitle
            ),
            description: intl.formatMessage(
                messages.fastshiftAssistanceModalErrorFlagDescription
            )
        };
    }, [intl]);

    const createMoveTicketCommentRequest = ({
        stargateClient,
        issueId,
        comment
    }: {
        stargateClient: IBxpWebStargateClient;
        issueId: string;
        comment: string;
    }) => {
        return new Promise((resolve, reject) =>
            createMoveTicketComment({
                stargateClient,
                issueId,
                comment
            })
                .then((response) => {
                    if (
                        !response ||
                        !response?.data?.customerSupport?.addComment
                    ) {
                        // if response payload is not valid, the comment was not created, regardless of the HTTP status code
                        reject(
                            new Error(
                                "Server failed to execute the request - comment was not created"
                            )
                        );
                    }
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                })
        );
    };

    // Search for items by ticket id, summary, site domain name, or product name
    const searchDropdownOptions = (option: Option, inputValue: string) => {
        const lowerCaseInputValue = inputValue.toLowerCase();
        return (
            option.label.toLowerCase().includes(lowerCaseInputValue) ||
            option.site.toLowerCase().startsWith(lowerCaseInputValue) ||
            option.product.toLowerCase().startsWith(lowerCaseInputValue)
        );
    };

    return (
        <Form<{ ticketId: Option; comment: string }>
            onSubmit={(data) => {
                // validate form fields
                if (!data.ticketId || !data.comment) {
                    const errors = {
                        ticketId: !data.ticketId
                            ? intl.formatMessage(
                                  messages.fastshiftAssistanceModalFieldIsRequiredMessage
                              )
                            : undefined,
                        comment: !data.comment
                            ? intl.formatMessage(
                                  messages.fastshiftAssistanceModalFieldIsRequiredMessage
                              )
                            : undefined
                    };
                    return errors;
                }

                sendClickSubmitButtonEvent(data.ticketId.value);
                return createMoveTicketCommentRequest({
                    stargateClient,
                    issueId: data.ticketId.value,
                    comment: data.comment
                })
                    .then(() => {
                        showFlag(
                            successFlag({
                                ticketId: data.ticketId.value
                            })
                        );
                        sendDisplayFlagEvent({
                            supportTicketId: data.ticketId.value,
                            success: true
                        });
                        handleCloseModal();
                    })
                    .catch((error) => {
                        console.error(error);
                        sendDisplayFlagEvent({
                            supportTicketId: data.ticketId.value,
                            success: false
                        });
                        showFlag(errorFlag);
                    });
            }}
        >
            {({ formProps, submitting }) => (
                <form {...formProps}>
                    <ModalHeader>
                        <Heading size="medium">
                            <FormattedMessage
                                {...messages.fastshiftAssistanceModalTitle}
                            />
                        </Heading>

                        <CloseModalButton onCloseClick={handleCloseModal} />
                    </ModalHeader>
                    <ModalBody>
                        <Stack space="space.200">
                            <HelperMessage>
                                <FormattedMessage
                                    {...messages.fastshiftAssistanceModalSubtitleNote}
                                />
                                <RequiredAsterisk />
                            </HelperMessage>
                            <Stack space="space.100">
                                <Text>
                                    <FormattedMessage
                                        {...messages.fastshiftAssistanceModalDescription}
                                    />
                                </Text>

                                <Field<ValueType<Option>>
                                    name="ticketId"
                                    label={intl.formatMessage(
                                        messages.fastshiftAssistanceModalDropdownLabel
                                    )}
                                    aria-required={true}
                                    isRequired
                                >
                                    {({
                                        fieldProps: { id, ...rest },
                                        error
                                    }) => (
                                        <>
                                            <Select<Option>
                                                inputId={id}
                                                {...rest}
                                                options={
                                                    customerSupportRequestOptions
                                                }
                                                maxMenuHeight={200}
                                                isLoading={
                                                    isCustomerSupportRequestsLoading
                                                }
                                                {...(isModalExtendedSearchEnabled
                                                    ? {
                                                          filterOption: (
                                                              option,
                                                              inputValue
                                                          ) => {
                                                              return searchDropdownOptions(
                                                                  option.data,
                                                                  inputValue
                                                              );
                                                          }
                                                      }
                                                    : {})}
                                            />
                                            {!error && (
                                                <HelperMessage>
                                                    <FormattedMessage
                                                        {...messages.fastshiftAssistanceModalDropdownHint}
                                                    />
                                                </HelperMessage>
                                            )}
                                            {error && (
                                                <ErrorMessage>
                                                    {error}
                                                </ErrorMessage>
                                            )}
                                        </>
                                    )}
                                </Field>
                                <Field
                                    aria-required={true}
                                    name="comment"
                                    label={intl.formatMessage(
                                        messages.fastshiftAssistanceModalCommentLabel
                                    )}
                                    isRequired
                                >
                                    {({ fieldProps, error }) => (
                                        <>
                                            {/* @ts-ignore */}
                                            <TextArea
                                                autoComplete="off"
                                                {...fieldProps}
                                                isRequired={false} // disables default textarea behaviour to keep UX consistent
                                                resize="auto"
                                                maxHeight="30vh"
                                                minimumRows={7}
                                            />
                                            {error && (
                                                <ErrorMessage>
                                                    {error}
                                                </ErrorMessage>
                                            )}
                                        </>
                                    )}
                                </Field>
                            </Stack>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonGroup>
                            <Button
                                appearance="subtle"
                                onClick={handleCloseModal}
                            >
                                <FormattedMessage
                                    {...messages.fastshiftAssistanceModalCancelButton}
                                />
                            </Button>
                            <LoadingButton
                                type="submit"
                                appearance="primary"
                                isLoading={submitting}
                            >
                                <FormattedMessage
                                    {...messages.fastshiftAssistanceModalSendButton}
                                />
                            </LoadingButton>
                        </ButtonGroup>
                    </ModalFooter>
                </form>
            )}
        </Form>
    );
};

const EmptyAssistanceModal = ({
    handleCloseModal
}: {
    handleCloseModal: () => void;
}) => {
    return (
        <>
            <ModalHeader>
                <Heading size="medium">
                    <FormattedMessage
                        {...messages.fastshiftAssistanceModalTitle}
                    />
                </Heading>
                <CloseModalButton onCloseClick={handleCloseModal} />
            </ModalHeader>
            <ModalBody>
                <Stack space="space.200">
                    <Text>
                        <FormattedMessage
                            {...messages.fastshiftAssistanceModalEmptyDescription}
                        />
                    </Text>
                </Stack>
            </ModalBody>
            <ModalFooter>
                <Button appearance="subtle" onClick={handleCloseModal}>
                    <FormattedMessage
                        {...messages.fastshiftAssistanceModalCancelButton}
                    />
                </Button>
                <Button
                    appearance="primary"
                    target="_blank"
                    href={`${SAC_URL}/contact`}
                >
                    <FormattedMessage
                        {...messages.fastshiftAssistanceModalEmptyModalRaiseTicketButton}
                    />
                </Button>
            </ModalFooter>
        </>
    );
};

// until we have a design for the loading state, we will use a simple spinner
const LoadingState = ({
    handleCloseModal
}: {
    handleCloseModal: () => void;
}) => {
    return (
        <>
            <ModalHeader>
                <Heading size="medium">
                    <FormattedMessage
                        {...messages.fastshiftAssistanceModalTitle}
                    />
                </Heading>
                <CloseModalButton onCloseClick={handleCloseModal} />
            </ModalHeader>
            <ModalBody>
                <Box padding="space.200">
                    <Spinner
                        testId="spinner"
                        interactionName="load"
                        label="Loading"
                        size="medium"
                    />
                </Box>
            </ModalBody>
        </>
    );
};

export const RequestAssistanceModal = ({
    isModalOpen,
    setIsModalOpen
}: {
    isModalOpen: boolean;
    setIsModalOpen: (isModalOpen: boolean) => void;
}) => {
    const { filteredTickets, isPending } = useTicketsFilteredBySelectedOrg();
    const { fireTrackEvent } = useFireAnalyticsEvents();

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const openTicketCount = filteredTickets.filter(
        (ticket) => ticket.status.category === "OPEN"
    ).length;

    React.useEffect(() => {
        if (!isPending && isModalOpen) {
            fireTrackEvent({
                action: ActionKey.DISPLAYED,
                actionSubject: ActionSubjectKey.MODAL,
                actionSubjectId:
                    ActionSubjectIdKey.REQUEST_FASTSHIFT_ASSISTANCE_MODAL,
                source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
                attributes: {
                    isEmpty: openTicketCount === 0,
                    supportTicketCount: openTicketCount
                }
            });
        }
    }, [isPending, fireTrackEvent, openTicketCount, isModalOpen]);

    return (
        <ModalTransition>
            {isModalOpen && (
                <Modal onClose={() => setIsModalOpen(false)}>
                    {isPending ? (
                        <LoadingState handleCloseModal={handleCloseModal} />
                    ) : openTicketCount > 0 ? (
                        <FastshiftAssistanceModalForm
                            handleCloseModal={handleCloseModal}
                        />
                    ) : (
                        <EmptyAssistanceModal
                            handleCloseModal={handleCloseModal}
                        />
                    )}
                </Modal>
            )}
        </ModalTransition>
    );
};
